import type { KarlAskGroup } from "../karlAsks";
import { emptyKarlAskQueueState } from "./types";
import type { KarlAskOutcome, KarlAskQueueState, KarlAskRecord } from "./types";

export interface KarlAskQueueStorageKeyArgs {
  projectId: string;
  planName: string;
  group: KarlAskGroup;
}

export type KarlAskQueueStorageKey = (
  args: KarlAskQueueStorageKeyArgs,
) => string;

export interface ReadKarlAskQueueStateArgs {
  projectId: string | null | undefined;
  planName: string | null | undefined;
  group: KarlAskGroup;
}

export type ReadKarlAskQueueState = (
  args: ReadKarlAskQueueStateArgs,
) => KarlAskQueueState;

export interface WriteKarlAskQueueStateArgs {
  projectId: string | null | undefined;
  planName: string | null | undefined;
  group: KarlAskGroup;
  state: KarlAskQueueState;
}

export type ClearKarlAskQueueState = (
  args: ReadKarlAskQueueStateArgs,
) => void;

export type WriteKarlAskQueueState = (args: WriteKarlAskQueueStateArgs) => void;

const STORAGE_VERSION = 1;

interface StoredAskQueue {
  version: number;
  history: KarlAskRecord[];
  deferred: string[];
}

const OUTCOMES: KarlAskOutcome[] = ["accepted", "declined", "committed"];

// Each group is its own run of questions, so sources and grouping must never
// land on the same key.
export const karlAskQueueStorageKey: KarlAskQueueStorageKey = ({
  projectId,
  planName,
  group,
}) => `karl.asks.v${STORAGE_VERSION}.${projectId}.${planName}.${group}`;

const isRecordObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isAskRecord = (value: unknown): value is KarlAskRecord => {
  if (!isRecordObject(value)) return false;
  if (typeof value.askId !== "string" || !value.askId) return false;
  if (typeof value.answerId !== "string") return false;
  if (!OUTCOMES.includes(value.outcome as KarlAskOutcome)) return false;
  if (value.confirmKey !== undefined && typeof value.confirmKey !== "string") {
    return false;
  }
  if (value.revert !== undefined && !isRecordObject(value.revert)) return false;
  return true;
};

const isStoredAskQueue = (value: unknown): value is StoredAskQueue => {
  if (!isRecordObject(value)) return false;
  if (value.version !== STORAGE_VERSION) return false;
  if (!Array.isArray(value.history) || !value.history.every(isAskRecord)) {
    return false;
  }
  if (
    !Array.isArray(value.deferred) ||
    !value.deferred.every((askId) => typeof askId === "string")
  ) {
    return false;
  }
  return true;
};

export const readKarlAskQueueState: ReadKarlAskQueueState = ({
  projectId,
  planName,
  group,
}) => {
  if (!projectId || !planName) return emptyKarlAskQueueState;
  try {
    const raw = window.localStorage.getItem(
      karlAskQueueStorageKey({ projectId, planName, group }),
    );
    if (!raw) return emptyKarlAskQueueState;
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredAskQueue(parsed)) return emptyKarlAskQueueState;
    // `armed` and `rewind` come back from the empty state, never from the
    // store: an arm waits on a click nothing is listening for any more, and
    // the cursor points into a list that is rebuilt from scratch.
    return {
      ...emptyKarlAskQueueState,
      history: parsed.history,
      deferred: parsed.deferred,
    };
  } catch {
    return emptyKarlAskQueueState;
  }
};

export const writeKarlAskQueueState: WriteKarlAskQueueState = ({
  projectId,
  planName,
  group,
  state,
}) => {
  if (!projectId || !planName) return;
  const stored: StoredAskQueue = {
    version: STORAGE_VERSION,
    history: state.history,
    deferred: state.deferred,
  };
  try {
    window.localStorage.setItem(
      karlAskQueueStorageKey({ projectId, planName, group }),
      JSON.stringify(stored),
    );
  } catch {
    // A full or blocked store is not worth breaking the review over.
  }
};

/** Removes a group's stored answers. A no-op when there is nothing to remove. */
export const clearKarlAskQueueState: ClearKarlAskQueueState = ({
  projectId,
  planName,
  group,
}) => {
  if (!projectId || !planName) return;
  try {
    window.localStorage.removeItem(
      karlAskQueueStorageKey({ projectId, planName, group }),
    );
  } catch {
    // A blocked store is not worth breaking the review over.
  }
};
